import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MapPin, ChevronDown } from 'lucide-react';
import { useRegion } from '../context/RegionContext';
import logo from '../assets/centmap_logo.jpeg';

const menuItems = [
  { label: 'Get the App!', to: '/app' },
  { label: 'Our team', to: '/team' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Privacy policy', to: '/privacy-policy' },
  { label: 'Support', to: '/support' },
];

export const TopBar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { regions, currentRegion, isAuto } = useRegion();

  // Check if we're on a map view (path matches a region slug)
  const isMapView = regions.some(region => location.pathname === `/${region.slug}`);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target as Node)) {
        setIsCityOpen(false);
      }
    };

    if (isMenuOpen || isCityOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, isCityOpen]);

  const handleCitySelect = (slug: string) => {
    // Just navigate - MapPage will update the context based on URL
    // This avoids timing issues with context update vs navigation
    navigate(`/${slug}`);
    setIsCityOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
      <div className="flex items-center justify-between h-16 px-4 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src={logo}
            alt="Centmap"
            className="h-9 w-9 rounded-lg"
          />
          <span className="text-lg font-semibold text-gray-900 hidden sm:block">
            Centmap
          </span>
        </Link>

        {/* City Selector - positioned in center, only visible on map view */}
        {isMapView && (
          <div className="relative" ref={cityRef}>
            <button
              onClick={() => setIsCityOpen(!isCityOpen)}
              className="flex items-center gap-1.5 h-11 px-3 rounded-lg border border-gray-200 hover:border-[#167062] hover:bg-gray-50 transition-colors min-w-0"
              aria-label="Select city"
              aria-expanded={isCityOpen}
              style={{ minHeight: '44px' }}
            >
              <MapPin className="w-4 h-4 text-[#167062] flex-shrink-0" />
              <span className="text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none">
                {currentRegion?.name || 'Select city'}
              </span>
              {isAuto && currentRegion && (
                <span className="text-xs text-gray-500 hidden sm:inline">(Auto)</span>
              )}
              <ChevronDown
                className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                  isCityOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* City Dropdown */}
            {isCityOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 max-h-80 overflow-y-auto">
                {regions.map((region) => (
                  <button
                    // Keyed and matched by slug: Firestore ids are not unique
                    // (Bangkok and Singapore are both 1).
                    key={region.slug}
                    onClick={() => handleCitySelect(region.slug)}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                      currentRegion?.slug === region.slug
                        ? 'bg-[#167062]/10 text-[#167062] font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {region.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Hamburger Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="flex items-center gap-3 p-2 border border-gray-200 rounded-full hover:shadow-md transition-shadow"
            aria-label="Menu"
            aria-expanded={isMenuOpen}
          >
            {/* Hamburger Icon */}
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2">
              {menuItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
